import { type FC, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Combobox, SelectTrigger } from "@similarweb/ui-components";
import { Flex, TruncatedText } from "@similarweb/ui-components/foundations";

import { JOB_REFRESH_PERIOD_OPTIONS } from "../../../constants";
import type { CrmEnrichmentI18nKeys } from "../../../types/translations";
import type { CrmEnrichmentJobRefreshPeriod } from "../../../types/types";

type Props = {
    selectedJobRefreshPeriod: CrmEnrichmentJobRefreshPeriod;
    translationKeys: CrmEnrichmentI18nKeys;
    onSelectJobRefreshPeriod: (period: CrmEnrichmentJobRefreshPeriod) => void;
};

const RefreshPeriodDropdown: FC<Props> = ({ selectedJobRefreshPeriod, onSelectJobRefreshPeriod, translationKeys }) => {
    const { translate } = useTranslate();
    const [isOpen, setIsOpen] = useState(false);

    const handleSelect = (period: CrmEnrichmentJobRefreshPeriod): void => {
        setIsOpen(false);
        onSelectJobRefreshPeriod(period);
    };

    const buttonLabel = translate(translationKeys.refreshPeriod(selectedJobRefreshPeriod));

    return (
        <Combobox
            data-automation="crm-enrichment-refresh-period-dropdown"
            placement="bottom-start"
            isOpen={isOpen}
            onOpenChange={setIsOpen}
        >
            <Combobox.Trigger>
                <SelectTrigger>
                    <Flex
                        gap="8px"
                        padding="0 12px 0 16px"
                        alignItems="center"
                    >
                        <TruncatedText
                            fontVariant="listItem1"
                            width="130px"
                            textAlign="start"
                        >
                            {buttonLabel}
                        </TruncatedText>
                    </Flex>
                </SelectTrigger>
            </Combobox.Trigger>

            <Combobox.Content>
                {JOB_REFRESH_PERIOD_OPTIONS.map((option) => (
                    <Combobox.Option
                        key={option}
                        id={option}
                        textValue={translate(translationKeys.refreshPeriod(option))}
                        isSelected={option === selectedJobRefreshPeriod}
                        onClick={() => handleSelect(option)}
                    >
                        <Combobox.OptionLabel>{translate(translationKeys.refreshPeriod(option))}</Combobox.OptionLabel>
                    </Combobox.Option>
                ))}
            </Combobox.Content>
        </Combobox>
    );
};

export default RefreshPeriodDropdown;
